import { Property, SecondContact } from '../../../../models/Property';
import { Document, Page, Text, View, StyleSheet, Image, Font } from '@react-pdf/renderer';
import mobileLogo from '../../../../img/watco-vertical-logo.jpg';
import {
	additionalAmenitiesFormatter,
	checkDescriptionLength,
	checkWatcoContact,
	displayBuilding,
	incentiveFormatter,
	propertyHighwayFormatter,
	propertyNameFormatter,
	specialtyFeaturesFormatter,
	toFormattedString,
	utilityStringBuilder,
	zoningFormatter
} from '../../../../utilities/propertyUtilities';
//@ts-ignore
import montRegular from '../../../../fonts/Montserrat-Regular.ttf';
//@ts-ignore
import montLight from '../../../../fonts/Montserrat-ExtraLight.ttf';
//@ts-ignore
import montMedium from '../../../../fonts/Montserrat-Medium.ttf';

Font.register({
	family: 'Montserrat',
	fonts: [
		{
			family: 'Montserrat',
			fontStyle: 'normal',
			src: montRegular
		},
		{
			family: 'Montserrat',
			fontStyle: 'normal',
			fontWeight: 'normal',
			src: montLight
		},
		{
			family: 'Montserrat',
			fontStyle: 'normal',
			fontWeight: 'bold',
			src: montMedium
		}
	]
});

const styles = StyleSheet.create({
	page: {
		display: 'flex',
		flexDirection: 'column',
		margin: '0 auto',
		width: '80% !important',
		height: '100% !important',
		fontFamily: 'Montserrat'
	},
	header: {
		display: 'flex',
		flexDirection: 'row',
		justifyContent: 'space-between',
		alignItems: 'center',
		height: '100px',
		width: '100%',
		padding: '16 32 0 32'
	},
	propertyInfo: {
		display: 'flex',
		flexDirection: 'column'
	},
	propertyName: {
		fontSize: '20px',
		fontWeight: 'bold',
	},
	propertyAddress: {
		fontSize: '12px',
		margin: '2px 0'
	},
	propertySize: {
		display: 'flex',
		flexDirection: 'row',
		justifyContent: 'flex-start',
		fontSize: '16px'
	},
	divider: {
		borderBottom: '4px solid #FFC907',
		width: '100%'
	},
	watcoLogo: {
		height: '60px',
		objectFit: 'scale-down',
		marginBottom: '6px'
	},
	watcoLogo2: {
		height: "52px",
		objectFit: "scale-down",
		margin: "0 auto"
	},
	pageRow: {
		display: 'flex',
		flexDirection: 'row',
		margin: '16px 32px 0',
		gap: '8px'
	},
	pageColumn: {
		display: 'flex',
		flexDirection: 'column',
		maxWidth: '100%',
		flex: '1'
	},
	primaryImageContainer: {
		textAlign: 'left'
	},
	primaryImage: {
		maxHeight: '110%',
		maxWidth: '110%',
		objectFit: 'contain'
	},
	image: {
		height: '110%',
		width: '110%'
	},
	sectionHeader: {
		color: '#58595B',
		margin: '18px 0 4px 0',
		fontWeight: 'bold'
	},
	sectionValue: {
		display: 'flex',
		flexDirection: 'row',
		fontSize: '12px'
	}
});

interface PropertyDocumentProps {
	property: Property;
}

export default function PropertyDocument({ property }: PropertyDocumentProps) {
	const { siteBuildingAddress, siteBuildingCity, siteBuildingState, siteBuildingZipCode } = property;
	const addressString = `${siteBuildingAddress ? siteBuildingAddress + ' | ' : ''}${siteBuildingCity + ', ' + siteBuildingState + ' ' + siteBuildingZipCode}`;

	let coordString = '';
	if (!property.geometry || !property.geometry.extent || !property.geometry.extent.center) {
		coordString = 'N/A';
	} else {
		const geo = property.geometry;
		const extentCenter = geo.extent.center;
		const coords: Record<string, string | null> = {
			latitude: extentCenter.latitude.toFixed(6),
			longitude: extentCenter.longitude.toFixed(6)
		};
		coordString = coords.latitude && coords.longitude ? `${coords.latitude} | ${coords.longitude}` : 'N/A';
	}

	const { site, building } = property;

	const propertyName = propertyNameFormatter(property);

	let propertyStatus: string = property.site?.siteSaleOrLease ? property.site?.siteSaleOrLease : '';
	if (propertyStatus.toLowerCase().split(' ').includes('both')) {
		propertyStatus = 'For Sale/Lease';
	}

	let propertyTypeSize;
	let propertySize;
	const propertySizeHelper = (type: 'Site' | 'Building' | undefined) => {
		switch (type) {
			case 'Building':
				propertyTypeSize = 'Building Square Footage: ';
				propertySize = `${toFormattedString(property.building?.areaSqFt ?? 0)}`;
				break;
			case 'Site':
				propertyTypeSize = 'Site Area Acres: ';
				propertySize = `${toFormattedString(property.site?.areaAcres ?? 0)}`;
				break;
			default:
				propertySize = undefined;
		}
	}

	let type: 'Site' | 'Building' | undefined;

	if (property.type === 'Site' && property.site?.areaAcres) {
		type = 'Site';
	} else if (property.type === 'Building' && property.building?.areaSqFt) {
		type = 'Building';
	} else if (property.type === 'Both') {
		if (property.site?.areaAcres) {
			type = 'Site';
		} else if (property.building?.areaSqFt) {
			type = 'Building';
		}
	} else { type = undefined; }
	propertySizeHelper(type);

	let floodPlain: string;
	if (property.inFloodPlain?.toLowerCase() === 'no') {
		floodPlain = 'No';
	} else if (property.inFloodPlain?.toLowerCase() === 'yes') {
		floodPlain = `Yes - ${property.floodPlainDesignation}`;
	} else {
		floodPlain = 'Unknown';
	}

	const { otherWatcoContact, brokerContact, brokerEmail, watcoContact, watcoContactOther } = property;
	const secondContact: SecondContact | null = otherWatcoContact
		? { contact: otherWatcoContact, email: 'industrialdevelopment@watco.com', phone: undefined, companyName: null }
		: brokerContact
			? { contact: brokerContact, email: brokerEmail?.toLowerCase(), phone: property.brokerContactPhone, companyName: property.companyName }
			: null;

	//const foreignTradeZone = site?.specialtyFeatures?.includes('foreign_trade_zone') ? 'Yes' : 'No';

	//Utilities
	const internet = utilityStringBuilder(site?.cabelInternetProvider, site?.cabelInternet);
	const electric = utilityStringBuilder(site?.electricalProvider, site?.electricalCapcity);
	const gas = utilityStringBuilder(site?.gasProvider, site?.gasCapacity);
	const stormWater = utilityStringBuilder(site?.stormWaterProvider, site?.stormWaterCapacity);
	const wasteWater = utilityStringBuilder(site?.sewerProvider, site?.sewerCapacity);
	const water = utilityStringBuilder(site?.waterProvider, site?.waterCapacity);

	const amenities = additionalAmenitiesFormatter(property.additionalAmenities, property.additionalAmenitiesOther);

	return (
		<Document title={propertyName} >
			<Page size="A4" style={styles.page}>
				<View style={styles.header}>
					<View style={styles.propertyInfo}>
						<Text style={styles.propertyName}>{propertyName}</Text>
						<Text style={styles.propertyAddress}>{addressString}</Text>
						<View style={styles.propertySize}>
							<Text style={{ fontWeight: 'bold', fontSize: '12px' }}>{propertyTypeSize}</Text>
							<Text style={{ fontSize: '12px' }}>{propertySize}</Text>
						</View>
						{/* UPDATE */}
						{propertyStatus &&
							<Text style={{ color: '#327B8F', fontWeight: 'bold', margin: '4px 0', fontSize: '14px' }}>{propertyStatus}</Text>
						}
					</View>

					<Image src={mobileLogo} style={styles.watcoLogo} />
				</View>
				<View style={styles.divider} />

				<View style={styles.pageRow}>
					<View style={styles.pageColumn}>
						<View style={{ ...styles.primaryImageContainer, maxWidth: '250px', maxHeight: '275px' }}>
							{property.primaryImage && <Image src={property.primaryImage} style={styles.primaryImage} />}
						</View>

						<View style={{ maxWidth: '250px' }}>
							<Text style={styles.sectionHeader}>Description</Text>
							<Text style={{ fontSize: '12px' }}>{property.siteBuildingDescription?.replaceAll("\t", " ")}</Text>
						</View>
						{/* Contact */}
						<View>
							<Text style={styles.sectionHeader}>Contact Information</Text>
							<View style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
								<View>
									<Text style={{ fontSize: '12px' }}>{checkWatcoContact(watcoContact, watcoContactOther)}</Text>
									<Text style={{ fontSize: '12px' }}>Watco</Text>
									<Text style={{ fontSize: '12px', marginTop: '4px' }}>{property.watcoContactEmail}</Text>
									{property.watcoContactPhone && <Text style={{ fontSize: '12px', marginTop: '4px' }}>{property.watcoContactPhone}</Text>}
								</View>
							</View>
							{secondContact &&
								<View style={{ marginTop: '8px' }}>
									<Text style={{ fontSize: '12px', marginTop: '4px' }}>{secondContact.contact}</Text>
									{secondContact.companyName && <Text style={{ fontSize: '12px' }}>{secondContact.companyName}</Text>}
									{secondContact.email && <Text style={{ fontSize: '12px', marginTop: '4px' }}>{secondContact.email}</Text>}
									{secondContact.phone && <Text style={{ fontSize: '12px', marginTop: '4px' }}>{secondContact.phone}</Text>}
								</View>
							}
						</View>
						{/* Transportation description length less than 500*/}
						{checkDescriptionLength(property) && <TransPortaionDetails property={property}/>}
					</View>

					<View style={styles.pageColumn}>
						{/* Transportation description length greater than 500*/}
						{!checkDescriptionLength(property) && <TransPortaionDetails property={property}/>}
						{/* Building profile */}
						{displayBuilding(property) && property.building &&
							<View>
								<Text style={{ ...styles.sectionHeader, marginTop: checkDescriptionLength(property) ? '0px' : '18px' }}>Building Profile</Text>
								<View style={styles.sectionValue}>
									<Text style={{ fontWeight: 'ultrabold' }}>Tenancy: </Text>
									<Text style={{ flex: '1' }}>{property.building.tenancy ?? 'N/A'}</Text>
								</View>
								<View style={styles.sectionValue}>
									<Text style={{ fontWeight: 'bold' }}>LTB Ratio: </Text>
									<Text style={{ flex: '1' }}>{property.building.ltbRatio ?? '0'}</Text>
								</View>
								<View style={styles.sectionValue}>
									<Text style={{ fontWeight: 'bold' }}>Building SF: </Text>
									<Text style={{ flex: '1' }}>{toFormattedString(property.building.areaSqFt) ?? '0'}</Text>
								</View>
								<View style={styles.sectionValue}>
									<Text style={{ fontWeight: 'bold' }}>Building Type: </Text>
									<Text style={{ flex: '1', textTransform: "capitalize" }}>{property.building.buildingType ?? 'N/A'}</Text>
								</View>
								<View style={styles.sectionValue}>
									<Text style={{ fontWeight: 'bold' }}>Warehouse SF: </Text>
									<Text style={{ flex: '1' }}>{toFormattedString(property.building.warehouseSqFt) ?? '0'}</Text>
								</View>
								<View style={styles.sectionValue}>
									<Text style={{ fontWeight: 'extrabold' }}>Office SF: </Text>
									<Text style={{ flex: '1' }}>{toFormattedString(property.building.officeSqFt) ?? '0'}</Text>
								</View>
								<View style={styles.sectionValue}>
									<Text style={{ fontWeight: 'extrabold' }}>Number of Docks of Dock Doors: </Text>
									<Text style={{ flex: '1' }}>{property.building.numberOfDockDoors ?? '0'}</Text>
								</View>
								<View style={styles.sectionValue}>
									<Text style={{ fontWeight: 'extrabold' }}>Number of Drive-in Doors: </Text>
									<Text style={{ flex: '1' }}>{property.building.numberOfDriveInDoors ?? '0'}</Text>
								</View>
							</View>
						}
						{/* Site Profile */}
						{property.site &&
							<View>
								<Text style={{ ...styles.sectionHeader, marginTop: !displayBuilding(property) && checkDescriptionLength(property) ? '0px' : '18px' }}>Site Profile</Text>
								<View style={styles.sectionValue}>
									<Text style={{ fontWeight: 'bold' }}>Acreage: </Text>
									<Text style={{ flex: '1' }}>{property.site.areaAcres ?? '0'}</Text>
								</View>
								<View style={styles.sectionValue}>
									<Text style={{ fontWeight: 'bold' }}>Within City Limits: </Text>
									<Text style={{ flex: '1' }}>{property.site.withinCity ?? 'N/A'}</Text>
								</View>
								<View style={styles.sectionValue}>
									<Text style={{ fontWeight: 'bold' }}>County: </Text>
									<Text style={{ flex: '1' }}>{property.site.county ?? 'N/A'}</Text>
								</View>
								<View style={styles.sectionValue}>
									<Text style={{ fontWeight: 'bold' }}>Latitude/Longitude: </Text>
									<Text style={{ flex: '1' }}>{coordString ?? 'N/A'}</Text>
								</View>
								<View style={styles.sectionValue}>
									<Text style={{ fontWeight: 'extrabold' }}>Rail Access: </Text>
									<Text style={{ flex: '1' }}>{property.site.siteRailServed ?? 'N/A'}</Text>
								</View>
								<View style={styles.sectionValue}>
									<Text style={{ fontWeight: 'extrabold' }}>Outdoor Storage: </Text>
									<Text style={{ flex: '1' }}>{property.building?.outdoorStorage ?? 'N/A'}</Text>
								</View>
								<View style={styles.sectionValue}>
									<Text style={{ fontWeight: 'extrabold' }}>Transloading Available: </Text>
									<Text style={{ flex: '1' }}>{property.transloading}</Text>
								</View>
								<View style={styles.sectionValue}>
									<Text style={{ fontWeight: 'extrabold' }}>Specialty Features: </Text>
									<Text style={{ flex: '1' }}>{specialtyFeaturesFormatter(property.site) ?? 'N/A'}</Text>
								</View>
								<View style={styles.sectionValue}>
									<Text style={{ fontWeight: 'extrabold' }}>Incentives: </Text>
									<Text style={{ flex: '1' }}>{incentiveFormatter(property.incentives) ?? 'N/A'}</Text>
								</View>
								<View style={styles.sectionValue}>
									<Text style={{ fontWeight: 'extrabold' }}>Flood Zone: </Text>
									<Text style={{ flex: '1' }}>{floodPlain}</Text>
								</View>
								{/*<View style={styles.sectionValue}>
									<Text style={{ fontWeight: 'extrabold' }}>Foreign Trade Zone: </Text>
									<Text style={{ flex: '1' }}>{foreignTradeZone}</Text>
								</View>*/}
								<View style={styles.sectionValue}>
									<Text style={{ fontWeight: 'extrabold' }}>Zoning: </Text>
									<Text style={{ flex: '1' }}>{zoningFormatter(property.site) ?? 'N/A'}</Text>
								</View>
								<View style={styles.sectionValue}>
									<Text style={{ fontWeight: 'extrabold' }}>Additional Amenities: </Text>
									<Text style={{ flex: '1' }}>{amenities ?? 'N/A'}</Text>
								</View>
							</View>
						}
						{/* Utilities */}
						<View>
							<Text style={styles.sectionHeader}>Utilities</Text>
							<View style={styles.sectionValue}>
								<Text style={{ fontWeight: 'bold' }}>Cable/Internet: </Text>
								<Text style={{ flex: '1' }}>{internet ?? 'N/A'}</Text>
							</View>
							<View style={styles.sectionValue}>
								<Text style={{ fontWeight: 'bold' }}>Electric: </Text>
								<Text style={{ flex: '1' }}>{electric ?? 'N/A'}</Text>
							</View>
							<View style={styles.sectionValue}>
								<Text style={{ fontWeight: 'bold' }}>Natural Gas: </Text>
								<Text style={{ flex: '1' }}>{gas ?? 'N/A'}</Text>
							</View>
							<View style={styles.sectionValue}>
								<Text style={{ fontWeight: 'bold' }}>Stormwater: </Text>
								<Text style={{ flex: '1' }}>{stormWater ?? 'N/A'}</Text>
							</View>
							<View style={styles.sectionValue}>
								<Text style={{ fontWeight: 'bold' }}>Wastewater: </Text>
								<Text style={{ flex: '1' }}>{wasteWater ?? 'N/A'}</Text>
							</View>
							<View style={styles.sectionValue}>
								<Text style={{ fontWeight: 'bold' }}>Water: </Text>
								<Text style={{ flex: '1' }}>{water ?? 'N/A'}</Text>
							</View>
						</View>
					</View>
				</View>
			</Page>
			<ImagePages property={property} />
		</Document>
	)
}

interface ImagePagesProps {
	property: Property;
}

function ImagePages({ property }: ImagePagesProps) {

	const images = property.images.filter(url => url !== property.primaryImage);

	if (images.length === 0) {
		return null;
	} else {
		const pageImages = Array.from({ length: Math.ceil(images.length / 4) },
			(_, i) => images.slice(i * 4, i * 4 + 4)
		);

		return (
			<>
				{
					pageImages.map((arr, index) => (
						<Page key={`Property-Document--Image-Page-${index + 1}`} size="A4" style={styles.page}>
							<View style={styles.header}>
								<View style={{ ...styles.propertyInfo, marginTop: '39px' }}>
									<Text style={{ ...styles.propertyName }}>Property Images</Text>
								</View>
								<Image src={mobileLogo} style={styles.watcoLogo} />
							</View>
							<View style={styles.divider} />
							<View style={{ ...styles.pageRow }}>
								{
									Array.from({ length: Math.ceil(arr.length / 2) },
										(_, i) => arr.slice(i * 2, i * 2 + 2)
									).map((images, index) => (
										<View
											key={`Image-column-${index + 1}`}
											style={{ ...styles.pageColumn }}
										>
											{images.map((image, i) =>
												<View key={`Image-${i + 1} of ${images.length}`} style={{ maxWidth: '210px', maxHeight: '235px' }}>
													<Image
														src={image}
														style={{ ...styles.image, objectFit: 'contain' }} />
												</View>
											)}
										</View>
									))
								}
							</View>
						</Page>
					))
				}
			</>
		);
	}
}

interface TransPortaionDetailsProps {
	property: Property;
}
const TransPortaionDetails = ({property}: TransPortaionDetailsProps) => (
	<View>
		<Text style={{...styles.sectionHeader, marginTop: !checkDescriptionLength(property) ? '0px' : '18px'}}>Transportation</Text>
		<View style={styles.sectionValue}>
			<Text style={{ fontWeight: 'ultrabold' }}>Nearest Highway: </Text>
			<Text style={{ flex: 1 }}>{propertyHighwayFormatter(property) ?? 'N/A'}</Text>
		</View>
		<View style={styles.sectionValue}>
			<Text style={{ fontWeight: 'bold' }}>Nearest Interstate: </Text>
			<Text style={{ flex: 1 }}>{property.nearestInterstate ?? 'N/A'}</Text>
		</View>
		<View style={styles.sectionValue}>
			<Text style={{ fontWeight: 'bold' }}>Nearest Airport: </Text>
			<Text style={{ flex: 1 }}>{property.nearestAirport ?? 'N/A'}</Text>
		</View>
		<View style={styles.sectionValue}>
			<Text style={{ fontWeight: 'bold' }}>Nearest Port: </Text>
			<Text style={{ flex: 1 }}>{property.nearestPortName ?? 'N/A'}</Text>
		</View>
		<View style={styles.sectionValue}>
			<Text style={{ fontWeight: 'bold' }}>Rail-served: </Text>
			<Text style={{ flex: 1 }}>{property.site?.siteRailServed ?? property.building?.buildingRailServed ?? 'N/A'}</Text>
		</View>
		<View style={styles.sectionValue}>
			<Text style={{ fontWeight: 'bold' }}>Railroad: </Text>
			<Text style={{ flex: 1 }}>{property.site?.siteRailProvided ?? property.building?.buildingRailProvider ?? 'N/A'}</Text>
		</View>
	</View>
);